// "Today's Risk Snapshot" widget.
//
// A compact, dashboard-friendly card that shows the user's current
// success probability + the worst-case loss (10th-percentile path vs
// total contributions). Mirrors the `surface-card` look of the rest of
// the suite so it can be dropped into the dashboard sidebar OR rendered
// inline at the top of the goal-alignment page.

import type { RiskSnapshot } from '../api/goalAlignmentApi';

interface RiskSnapshotCardProps {
    snapshot: RiskSnapshot;
    /** Optional sub-line, e.g. "Updated just now". */
    note?: string;
}

function formatMoney(value: number): string {
    // The Risk Snapshot's "Worst-case loss" tile is the only place in
    // the suite where the dollar value can be genuinely small (a few
    // hundred dollars of early-month underwater drawdown is normal
    // for a conservative allocation on a short horizon). With the
    // suite-wide ``maximumFractionDigits: 0`` rounding, anything
    // under $0.50 rendered as "-$0", which looked like a hard zero
    // and confused users into reading it as "no risk at all".
    //
    // The fix: always emit two decimals so small values surface as
    // "-$0.42" / "-$12.50" instead of "-$0" / "-$13". For larger
    // values the two-decimal precision is still readable
    // ("-$1,234.56") and matches the long-form accounting convention.
    return value.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

export const RiskSnapshotCard = ({ snapshot, note }: RiskSnapshotCardProps) => {
    const prob = snapshot.probability_of_success;
    const probPct = (prob * 100).toFixed(0);
    const probTone =
        prob > 0.75
            ? 'text-emerald-600 dark:text-emerald-400'
            : prob > 0.5
            ? 'text-amber-600 dark:text-amber-400'
            : 'text-red-600 dark:text-red-400';

    return (
        <div className="rounded-2xl border-2 border-emerald-200 dark:border-emerald-900 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-950/40 dark:to-slate-900/30 p-4 shadow-sm">
            <div className="flex items-center justify-between gap-3">
                <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                        Today’s Risk Snapshot
                    </p>
                    <p className={`mt-1 text-3xl font-black tabular-nums ${probTone}`}>
                        {probPct}%
                    </p>
                    <p className="text-xs muted">success probability</p>
                </div>
                <div className="text-right">
                    <p className="text-xs font-bold uppercase tracking-wide muted">
                        Worst-case loss
                    </p>
                    <p className="mt-1 text-xl font-black tabular-nums text-red-600 dark:text-red-400">
                        −{formatMoney(snapshot.worst_case_loss)}
                    </p>
                    <p className="text-xs muted">
                        deepest underwater dip · worst 10 % of paths
                    </p>
                </div>
            </div>
            {note && <p className="mt-3 text-xs muted">{note}</p>}
        </div>
    );
};

export default RiskSnapshotCard;
